import React, { useState, useEffect, useContext } from "react";
import CustomButton from "../custom-button";
import ActivityButton from "../activity-button";
import LeadCallButton from "../../views/lead/details/lead-call-button";
import ProspectCallButton from "../../views/prospect/details/prospect-call-button";
import HomeownerCallButton from "../../views/homeowner/details/homeowner-call-button";
import { Auth } from "../../rbac/rbac";
import { CalenderContext } from "./context";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { LEAD, PROSPECT, HOMEOWNER, MARKETING_ONLY, REALTOR } from "../../utils/constants"
import { upperCaseFirstLetter, MonthYearComparison, getMonth, formatDate } from "../../utils/helpers";
import { AddHomeownerActivityContainer, AddLeadActivityContainer, AddActivityContainer, AddRealtorActivityContainer } from "../../views/common-lead-prospect-forms/activity-container";
import {
  FaChevronLeft,
  FaPlus,
  FaChevronRight,
  FaListAlt,
} from "react-icons/fa";
import "./calendar.css";
import Help from "../help";

/** 
 * @module CustomToolbarModule */

/**
 * This component is used in {@link BigCalendar} to render a custom toolbar. It contains legend and all the extra controls like changing the calendar view from monthly to weekly, changing the the day/month, or buttons for adding call/notes etc.
 * @param {Object} props 
 * @returns {JSX.Element}
 */
const CustomToolbar = ({ onNavigate, onView, view, label, ...props }) => {
  const [dropDownTitle, setDropDownTitle] = useState(
    upperCaseFirstLetter(view)
  );

  useEffect(() => {
    if (view !== dropDownTitle) {
      setDropDownTitle(upperCaseFirstLetter(view));
    }
  }, [view]);

  const { is_osc } = useContext(Auth);
  const { type, id, submitHandler, data, activeFilters, toggleFilter } = useContext(CalenderContext);
  const container = type === LEAD ? AddLeadActivityContainer
    : type === PROSPECT ? AddActivityContainer
      : type === HOMEOWNER ? AddHomeownerActivityContainer
        : type === REALTOR ? AddRealtorActivityContainer
          : null

  const navigate = (action) => {
    onNavigate(action);
  };

  /**
   * Create a clickable legend item
   * @param {string} filterKey - Key for the filter state
   * @param {string} legendId - CSS ID for the legend item
   * @param {string} label - Display label for the legend item
   * @param {boolean} isDashboard - Whether this is for dashboard calendar
   * @returns {JSX.Element}
   */
  const legendColorById = {
    "legend-appointments": "#92af00",
    "legend-homeowner-appointments": "#8a8a8a",
    "legend-realtor-appointments": "#d4ba2a",
    "legend-followups": "#4c7ef9",
    "legend-realtor-followups": "#A84264",
    "legend-phonecall": "#50E3C2",
    "legend-text": "#C48E48",
    "legend-traffic": "#00ff40",
    "legend-email": "#FF7A00",
    "legend-massemail": "#9400ff",
    "legend-ebomb": "#D0021B",
    "legend-webform": "#FF5E5E",
    "legend-walk-ins": "#a84264",
    "legend-saved-floor-plans": "#1B5E20",
    "legend-favorite-floor-plans": "#880E4F",
  };

  const createClickableLegendItem = (filterKey, legendId, label, isDashboard = false) => {
    const isActive = activeFilters && activeFilters[filterKey] !== false;
    const isClickable = isDashboard && toggleFilter;

    return (
      <span
        id={legendId}
        onClick={isClickable ? () => toggleFilter(filterKey) : undefined}
        style={{
          cursor: isClickable ? 'pointer' : 'default',
          opacity: isDashboard && !isActive ? 0.6 : 1,
          transition: 'opacity 0.2s ease'
        }}
        title={isClickable ? `Click to ${isActive ? 'hide' : 'show'} ${label.toLowerCase()}` : undefined}
      >
        {isDashboard && toggleFilter ? (
          <input
            type="checkbox"
            checked={!!isActive}
            onClick={(e) => {
              e.stopPropagation();
              toggleFilter(filterKey);
            }}
            id={`${legendId}-checkbox`}
            style={{
              accentColor: legendColorById[legendId],
              width: 12,
              height: 12,
              marginRight: 6,
              verticalAlign: 'middle',
              cursor: 'pointer'
            }}
          />
        ) : (
          <i />
        )}
        {label}
      </span>
    );
  };

  /**
   * This function checks where exactly unresponded webforms exist in previous or next month
   * @function
   * @inner
   * @memberof module:CustomToolbarModule
   * @returns {Array} show contains "previous" or "next" which informs about existence of unresponded webform
   * @see {@link module:CustomToolbarModule~CustomToolbar}
   */
  const checkVisibility = () => {
    let show = [];
    let labelDate = new Date(label)
    data &&
      data.Form_Submission_Details &&
      data.Form_Submission_Details.forEach((item) => {
        let date = new Date(item.created_at)
        let monthYearComparisonResult = MonthYearComparison(date, labelDate)
        if (
          monthYearComparisonResult.isBefore &&
          !item.responded_at
        ) {
          show.push("previous");
        } else if (
          monthYearComparisonResult.isAfter &&
          !item.responded_at
        ) {
          show.push("next");
        }
      });
    return show;
  };

  /**
   * This function renders icon with a tooltip whether unresponded is in next or previous months
   * @function
   * @inner
   * @memberof module:CustomToolbarModule
   * @param {String} comparator contains "next" or "previous"
   * @returns {JSX.Element}
   * @see {@link module:CustomToolbarModule~CustomToolbar}
   */
  const unrespondedWebformsIcon = (comparator) => {
    return type === "webform" ? (
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip style={{ zIndex: 10000 }}>
            unresponded form submission(s) in {comparator} months
          </Tooltip>
        }
      >
        <FaListAlt
          size={14}
          color="red"
        />
      </OverlayTrigger>
    ) : null;
  };

  /**
   * This function renders navigation buttons, to go to previous or next date
   * @function
   * @inner
   * @memberof module:CustomToolbarModule
   * @returns {JSX.Element}
   * @see {@link module:CustomToolbarModule~CustomToolbar}
   */
  const monthlyNavigationHeader = () => {
    let visible = checkVisibility();
    return (
      <div className="section-today">
        {visible.includes("previous") && unrespondedWebformsIcon("previous")}
        <CustomButton
          onClick={() => navigate("PREV")}
          id="fa-ChevronLeft"
          color="dark"
          btnIcon={<FaChevronLeft />}
          className={"arrowLeft"}
        />
        <button
          className="nav-btn"
          style={{ cursor: "default" }}
          type="button"
        >
          {getMonth(props.date)}
        </button>
        <CustomButton
          onClick={() => navigate("NEXT")}
          id="fa-ChevronRight"
          color="dark"
          btnIcon={<FaChevronRight />}
          className={"arrowRight"}
        />
        {visible.includes("next") && unrespondedWebformsIcon("next")}
      </div>
    )
  };

  return (
    <div className="rbc-toolbar-custom">
      <div className="d-flex flex-column mb-2">
        <div className="d-flex align-items-center mb-1">
          <h3 className="calendar-title m-0 mr-1">
            {id === 'dashboard-calendar'
              ? (is_osc() ? "Appointments & Follow-ups" : "Upcoming Appointments & Follow-ups")
              : "Activity Calendar"
            }
          </h3>
          <Help description="Click legend items to show/hide calendar items." />
        </div>
        <div className="calendar-legend">
          {type !=="webform"  && (
            <>
              {id === "dashboard-calendar" ? (
                <>
                  {createClickableLegendItem(
                    "appointments",
                    "legend-appointments",
                    "Appointments",
                    true
                  )}
                  {createClickableLegendItem(
                    "homeownerAppointments",
                    "legend-homeowner-appointments",
                    "Homeowner Appointments",
                    true
                  )}
                  {createClickableLegendItem(
                    "followups",
                    "legend-followups",
                    "Follow-ups",
                    true
                  )}
                </>
              ) : (
                <>
                  {type === REALTOR ? (
                    createClickableLegendItem(
                      "realtorAppointments",
                      "legend-realtor-appointments",
                      "Realtor Appointments",
                      true
                    )
                  ) : (
                    createClickableLegendItem(
                      "appointments",
                      "legend-appointments",
                      "Appointments",
                      true
                    )
                  )}
                  {createClickableLegendItem(
                    type === REALTOR ? "realtorFollowups" : "followups",
                    type === REALTOR ? "legend-realtor-followups" : "legend-followups",
                    type === REALTOR ? 'Realtor Follow-ups' : 'Follow-ups',
                    true
                  )}
                </>
              )}
            </>
          )}
          {(id === 'dashboard-calendar') && (
            <>
              {createClickableLegendItem(
                "realtorAppointments",
                "legend-realtor-appointments",
                "Realtor Appointments",
                true
              )}
              {createClickableLegendItem(
                "realtorFollowups",
                "legend-realtor-followups",
                "Realtor Follow-ups",
                true
              )}
              {createClickableLegendItem(
                "traffic",
                "legend-traffic",
                "Traffic",
                true
              )}
            </>
          )}
          {(id !== 'dashboard-calendar') && (
            <>
              {createClickableLegendItem(
                "ebomb",
                "legend-ebomb",
                "EBomb",
                true
              )}
            </>
          )}
          {(id !== 'dashboard-calendar' && type !== REALTOR) && (
            <>
              {createClickableLegendItem(
                "webform",
                "legend-webform",
                "Web Forms",
                true
              )}
              {createClickableLegendItem(
                "traffic",
                "legend-traffic",
                "Traffic",
                true
              )}
            </>
          )}
          {(id !== 'dashboard-calendar') && (
            <>
              {createClickableLegendItem(
                "email",
                "legend-email",
                "Email",
                true
              )}
            </>
          )}
          {(id !== 'dashboard-calendar' && type !== REALTOR) && (
            <>
              {createClickableLegendItem(
                "massemail",
                "legend-massemail",
                "Mass Email",
                true
              )}
              {createClickableLegendItem(
                "phoneCalls",
                "legend-phonecall",
                "Phone Calls",
                true
              )}
              {createClickableLegendItem(
                "text",
                "legend-text",
                "Text",
                true
              )}
            </>
          )}
          {((type === PROSPECT) || (type === MARKETING_ONLY)) && (
            createClickableLegendItem(
              "walkins",
              "legend-walk-ins",
              "Walk-Ins",
              true
            )
          )}
          {(id !== 'dashboard-calendar' && type !== REALTOR) && (
            <>
              {createClickableLegendItem(
                "savedFloorPlans",
                "legend-saved-floor-plans",
                "Saved Floor Plans",
                true
              )}
              {createClickableLegendItem(
                "favoriteFloorPlans",
                "legend-favorite-floor-plans",
                "Favorite Floor Plans",
                true
              )}
            </>
          )}

        </div>
      </div>
      <div className="calendar-header-controls">
        <div className="calendar-header-filters d-flex align-items-center">
          <p
            onClick={() => navigate("TODAY")}
            style={{ cursor: 'pointer' }}
          >
            {formatDate(new Date())}
          </p>
        </div>
        {monthlyNavigationHeader()}
        <div className="d-flex justify-content-end">
          {[LEAD, PROSPECT, HOMEOWNER, REALTOR].includes(type) ? (
            <ActivityButton
              {...props}
              data={data}
              btnIcon={<FaPlus />}
              type={type}
              function="CREATE"
              component={container}
              onSubmit={(message) => submitHandler(message)}
              className="btn btn-primary ml-2"
            />
          ) : null}

          {type === PROSPECT ? (
            <ProspectCallButton
              showButton
              id={id}
              onSubmit={(message) => submitHandler(message)}
              btnValue="ADD CALL"
              function={"ADD"}
              className="btn btn-primary ml-2"
            />
          ) : type === LEAD ? (
            <LeadCallButton
              showButton
              id={id}
              onSubmit={(message) => submitHandler(message)}
              btnValue="ADD CALL"
              function={"ADD"}
              className="btn btn-primary ml-2"
            />
          ) : type === HOMEOWNER ? (
            <>
              <HomeownerCallButton
                showButton
                id={id}
                onSubmit={(message) => submitHandler(message)}
                btnValue="ADD CALL"
                function={"ADD"}
                className="btn btn-primary ml-2"
              />
            </>
          ) : null}
        </div>
      </div>
      {/* <div className="calendar-month" >
        {monthlyNavigationHeader()}
      </div> */}
    </div>
  );
};

export default CustomToolbar;
